import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaBookOpen,
  FaCheck,
  FaClock,
  FaEraser,
  FaSave,
  FaSearch,
  FaTimes,
  FaUserGraduate,
} from "react-icons/fa";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/payroll.css";

const WEEKDAY_LABELS = {
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
  7: "Sunday",
};

//Created By Cursor
function subjectTypeLabel(type) {
  const t = String(type || "").trim().toLowerCase();
  if (t === "main") return "Main";
  if (t === "optional") return "Optional";
  return type ? String(type) : "";
}

//Created By Cursor
export default function MdlAssignTimetableCell({
  cell,
  classLabel,
  assignOptions,
  loadingOptions = false,
  saving,
  onClose,
  onSave,
}) {
  const [subjectId, setSubjectId] = useState(cell?.subject_id || "");
  const [staffId, setStaffId] = useState(cell?.staff_id || "");
  const [query, setQuery] = useState("");

  useEffect(() => {
    setSubjectId(cell?.subject_id || "");
    setStaffId(cell?.staff_id || "");
    setQuery("");
  }, [cell]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !saving && typeof onClose === "function") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const pairs = useMemo(() => {
    const list = Array.isArray(assignOptions?.pairs) ? assignOptions.pairs : [];
    if (list.length) return list;
    return (assignOptions?.subjects || []).map((s) => ({
      subject_id: s.subject_id,
      subject_name: s.subject_name,
      subject_type: s.subject_type,
      teachers: [],
    }));
  }, [assignOptions]);

  const filteredPairs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pairs;
    return pairs.filter((p) =>
      String(p.subject_name || p.subject_id || "")
        .toLowerCase()
        .includes(q)
    );
  }, [pairs, query]);

  const teachers = useMemo(() => {
    if (!subjectId) return [];
    const pair = pairs.find((p) => String(p.subject_id) === String(subjectId));
    const list = pair?.teachers || [];
    return [...list].sort((a, b) => {
      const avA = a.available === false ? 1 : 0;
      const avB = b.available === false ? 1 : 0;
      if (avA !== avB) return avA - avB;
      return String(a.staff_name || "").localeCompare(String(b.staff_name || ""));
    });
  }, [pairs, subjectId]);

  useEffect(() => {
    if (!staffId || !teachers.length) return;
    const picked = teachers.find((t) => String(t.staff_id) === String(staffId));
    if (picked && picked.available === false) {
      setStaffId("");
    }
  }, [teachers, staffId]);

  const selectedSubject = pairs.find(
    (p) => String(p.subject_id) === String(subjectId)
  );
  const selectedStaff = teachers.find(
    (t) => String(t.staff_id) === String(staffId)
  );

  const period = cell?.period || {};
  const dayLabel = WEEKDAY_LABELS[cell?.weekday] || "Day";
  const timeRange = `${String(period.start_time || "").slice(0, 5)}–${String(
    period.end_time || ""
  ).slice(0, 5)}`;

  const handlePickSubject = (id) => {
    setSubjectId(id);
    setStaffId("");
  };

  const handleClear = () => {
    if (typeof onSave === "function") {
      onSave({ subject_id: "", staff_id: "" });
    }
  };

  const handleSave = () => {
    if (selectedStaff && selectedStaff.available === false) {
      return;
    }
    if (typeof onSave === "function") {
      onSave({ subject_id: subjectId, staff_id: staffId });
    }
  };

  const modal = (
    <div
      className="tt-assign-mdl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tt-assign-mdl-title"
    >
      <button
        type="button"
        className="tt-assign-mdl__backdrop"
        aria-label="Close"
        disabled={saving}
        onClick={onClose}
      />
      <div className="tt-assign-mdl__panel">
        <header
          className="tt-assign-mdl__hero"
          style={{
            background: `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`,
          }}
        >
          <div className="tt-assign-mdl__hero-main">
            <div className="tt-assign-mdl__hero-icon" aria-hidden>
              <FaBookOpen />
            </div>
            <div className="min-w-0">
              <p className="tt-assign-mdl__eyebrow">Assign period</p>
              <h2 id="tt-assign-mdl-title" className="tt-assign-mdl__title">
                {period.period_name || "Period"} · {dayLabel}
              </h2>
              <div className="tt-assign-mdl__meta">
                {classLabel ? <span>{classLabel}</span> : null}
                {timeRange !== "–" ? (
                  <span>
                    <FaClock aria-hidden /> {timeRange}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="tt-assign-mdl__close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </header>

        <div className="tt-assign-mdl__body">
          <label className="tt-assign-mdl__search">
            <FaSearch aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search subjects…"
              aria-label="Search subjects"
            />
          </label>

          <section className="tt-assign-mdl__section" aria-label="Subject">
            <div className="tt-assign-mdl__section-head">
              <h3>Subject</h3>
              <span>{filteredPairs.length} available</span>
            </div>
            {filteredPairs.length === 0 ? (
              <p className="tt-assign-mdl__empty">
                No subjects found. Map staff subjects or class subjects first.
              </p>
            ) : (
              <div className="tt-assign-mdl__options" role="listbox">
                {filteredPairs.map((p) => {
                  const active = String(p.subject_id) === String(subjectId);
                  const typeLabel = subjectTypeLabel(p.subject_type);
                  return (
                    <button
                      key={p.subject_id}
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={`tt-assign-opt${active ? " is-active" : ""}`}
                      onClick={() => handlePickSubject(p.subject_id)}
                    >
                      <span className="tt-assign-opt__icon" aria-hidden>
                        <FaBookOpen />
                      </span>
                      <span className="tt-assign-opt__text">
                        <strong>{p.subject_name || p.subject_id}</strong>
                        <em>
                          {p.teachers?.length
                            ? `${p.teachers.length} teacher${
                                p.teachers.length === 1 ? "" : "s"
                              }`
                            : typeLabel || "No staff mapped"}
                        </em>
                      </span>
                      {typeLabel ? (
                        <span
                          className={`tt-assign-badge tt-assign-badge--${typeLabel.toLowerCase()}`}
                        >
                          {typeLabel}
                        </span>
                      ) : null}
                      {active ? (
                        <span className="tt-assign-opt__check" aria-hidden>
                          <FaCheck />
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className="tt-assign-mdl__section" aria-label="Staff">
            <div className="tt-assign-mdl__section-head">
              <h3>Teacher</h3>
              <span>
                {loadingOptions
                  ? "Checking availability…"
                  : subjectId
                  ? `${teachers.length} for this subject`
                  : "Pick a subject first"}
              </span>
            </div>
            {!subjectId ? (
              <p className="tt-assign-mdl__empty">
                Select a subject to see eligible staff from staff subject
                mapping.
              </p>
            ) : loadingOptions ? (
              <p className="tt-assign-mdl__empty">Loading staff availability…</p>
            ) : teachers.length === 0 ? (
              <p className="tt-assign-mdl__empty">
                No staff mapped for this subject in Staff Subject.
              </p>
            ) : (
              <div
                className="tt-assign-mdl__options tt-assign-mdl__options--staff"
                role="listbox"
              >
                {teachers.map((t) => {
                  const active = String(t.staff_id) === String(staffId);
                  const available = t.available !== false;
                  const typeLabel = subjectTypeLabel(t.subject_type);
                  return (
                    <button
                      key={t.staff_id}
                      type="button"
                      role="option"
                      aria-selected={active}
                      aria-disabled={!available}
                      disabled={!available}
                      title={
                        available
                          ? undefined
                          : `Not available — already in ${
                              t.busy_class_name || "another class"
                            }`
                      }
                      className={`tt-assign-opt tt-assign-opt--staff${
                        active ? " is-active" : ""
                      }${available ? "" : " is-busy is-disabled"}`}
                      onClick={() => {
                        if (!available) return;
                        setStaffId(t.staff_id);
                      }}
                    >
                      <span className="tt-assign-opt__icon" aria-hidden>
                        <FaUserGraduate />
                      </span>
                      <span className="tt-assign-opt__text">
                        <strong>{t.staff_name || t.staff_id}</strong>
                        <em className="tt-assign-opt__meta-row">
                          {typeLabel ? (
                            <span
                              className={`tt-assign-badge tt-assign-badge--${typeLabel.toLowerCase()}`}
                            >
                              {typeLabel}
                            </span>
                          ) : null}
                          {available ? (
                            <span className="tt-assign-avail is-free">
                              Available
                            </span>
                          ) : (
                            <span className="tt-assign-avail is-busy">
                              Not Available
                              {t.busy_class_name
                                ? ` · ${t.busy_class_name}`
                                : ""}
                            </span>
                          )}
                        </em>
                      </span>
                      {active ? (
                        <span className="tt-assign-opt__check" aria-hidden>
                          <FaCheck />
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {(selectedSubject || selectedStaff) && (
            <div className="tt-assign-mdl__summary" aria-live="polite">
              <span>
                {selectedSubject?.subject_name ||
                  selectedSubject?.subject_id ||
                  "No subject"}
              </span>
              <span aria-hidden>·</span>
              <span>
                {selectedStaff?.staff_name ||
                  selectedStaff?.staff_id ||
                  "No teacher"}
              </span>
              {selectedStaff && selectedStaff.available === false ? (
                <>
                  <span aria-hidden>·</span>
                  <span className="tt-assign-avail is-busy">
                    Busy
                    {selectedStaff.busy_class_name
                      ? ` in ${selectedStaff.busy_class_name}`
                      : ""}
                  </span>
                </>
              ) : null}
            </div>
          )}
        </div>

        <footer className="tt-assign-mdl__footer">
          <button
            type="button"
            className="tt-assign-mdl__btn tt-assign-mdl__btn--ghost"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="tt-assign-mdl__btn tt-assign-mdl__btn--danger"
            disabled={saving}
            onClick={handleClear}
            title="Clear this period slot"
          >
            <FaEraser />
            <span>Clear</span>
          </button>
          <button
            type="button"
            className="tt-assign-mdl__btn tt-assign-mdl__btn--primary"
            disabled={
              saving || (selectedStaff && selectedStaff.available === false)
            }
            onClick={handleSave}
          >
            <FaSave />
            <span>{saving ? "Saving…" : "Save"}</span>
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
